import React, { useRef, useState } from 'react';
import { Slider, SliderCategory } from '../types';
import { apiClient } from '../lib/api';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: Partial<Slider>) => void;
  initial?: Partial<Slider>;
  category: SliderCategory;
}

const SliderModal: React.FC<Props> = ({ open, onClose, onSave, initial, category }) => {
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [image, setImage] = useState(initial?.image || '');
  const [alt, setAlt] = useState(initial?.alt || '');
  const [link, setLink] = useState(initial?.link || '');
  const [sortOrder, setSortOrder] = useState(initial?.sort_order || 0);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadImage(file);
    }
    e.target.value = '';
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      await uploadImage(file);
    }
  };

  const uploadImage = async (file: File) => {
    setUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await apiClient.uploadImage(formData, category.image_folder);
      setImage(res.data.url);
    } catch (e: any) {
      setUploadError(e?.response?.data?.error || 'Ошибка загрузки картинки');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    await onSave({
      title,
      description,
      image,
      alt,
      link,
      sort_order: sortOrder,
      category_id: category.id,
    });
    setSaving(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center" onMouseDown={onClose}>
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onMouseDown={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-4">{initial ? 'Редактировать' : 'Создать'} слайд</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          {category.fields_structure.image && (
            <div>
              <label className="block text-sm font-medium mb-1">Картинка</label>
              <div
                className="border rounded p-2 flex items-center gap-4 cursor-pointer hover:bg-gray-50"
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={e => e.preventDefault()}
              >
                {image ? (
                  <img src={image} alt={alt} className="h-16 max-w-[120px] object-contain" />
                ) : (
                  <span className="text-gray-500">Перетащите файл или кликните для выбора</span>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
              {uploading && <div className="text-xs text-blue-600 mt-1">Загрузка...</div>}
              {uploadError && <div className="text-xs text-red-600 mt-1">{uploadError}</div>}
            </div>
          )}
          {category.fields_structure.alt && (
            <div>
              <label className="block text-sm font-medium mb-1">Alt</label>
              <input type="text" className="w-full border rounded px-3 py-2" value={alt} onChange={e => setAlt(e.target.value)} required={!!category.fields_structure.image} />
            </div>
          )}
          {category.fields_structure.title && (
            <div>
              <label className="block text-sm font-medium mb-1">Заголовок</label>
              <input type="text" className="w-full border rounded px-3 py-2" value={title} onChange={e => setTitle(e.target.value)} />
            </div>
          )}
          {category.fields_structure.description && (
            <div>
              <label className="block text-sm font-medium mb-1">Описание</label>
              <textarea className="w-full border rounded px-3 py-2" value={description} onChange={e => setDescription(e.target.value)} />
            </div>
          )}
          {category.fields_structure.link && (
            <div>
              <label className="block text-sm font-medium mb-1">Ссылка</label>
              <input type="text" className="w-full border rounded px-3 py-2" value={link} onChange={e => setLink(e.target.value)} />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Порядок</label>
            <input type="number" className="w-full border rounded px-3 py-2" value={sortOrder} onChange={e => setSortOrder(Number(e.target.value))} />
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button type="button" className="px-4 py-2 bg-gray-200 rounded" onClick={onClose} disabled={saving}>Отмена</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded" disabled={saving}>{initial ? 'Сохранить' : 'Создать'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SliderModal; 